import { ContractServices, type GetContractHistoryKlineReq } from 'network'
import { doubleMSocketService } from '../socket/ExchangeSocketService'
import { SocketEventName } from '../socket/SocketService'
import { useEnvStore } from './use-env'
import type { ResolutionString } from '../../../charting-library/charting_library'
import { useBaseInfoStore } from './use-base-info'
import { safeSecondToMillisecond } from 'utils'
import { useContractChangeSymbol } from '../hooks'
import { HandicapType, type KlineDirection } from '../constants/kline'
import { useExchangeInfoStore } from './use-exchange-info'

export const useDoubleContractKlineStore = defineStore('double-contract-kline', () => {
  const kline = ref<SocketResponse.HistoryKlineData[]>([])
  const currentKline = ref<SocketResponse.KlineFormatData>()
  const previousSpotKline = ref<SocketResponse.KlineFormatData>()
  const klineDirection = ref<KlineDirection>('equal')
  const loading = ref(true)
  const loadingHistoryKline = ref(false)
  const route = useRoute()
  const currentSymbolId = ref<SymbolId | undefined>(route.query.symbol ? Number(route.query.symbol) : 1000001)
  const envStore = useEnvStore()
  const currentResolution = ref<ResolutionString>('15' as ResolutionString)
  const baseInfoStore = useBaseInfoStore()
  const handicapType = ref<HandicapType>(HandicapType.All)
  const exchangeInfoStore = useExchangeInfoStore()

  /** 获取历史K线的结束时间 */
  const toTime = ref(0)

  const resetHistoryKlineLoading = () => {
    loadingHistoryKline.value = true
  }

  const getHistoryKline = async (data: GetContractHistoryKlineReq): Promise<SocketResponse.KlineFormatData[]> => {
    const res = await ContractServices.getHistoryKline(data)
    kline.value = res.data.kline
    currentSymbolId.value = Number(data.symbolId)

    const newData = res.data.kline.map((item) => ({
      time: safeSecondToMillisecond(item.t),
      open: item.o,
      high: item.h,
      low: item.l,
      close: item.c,
      volume: item.v,
    }))

    loadingHistoryKline.value = false

    return newData
  }

  const formatSocketKline = (data: SocketResponse.SpotKlineData): SocketResponse.KlineFormatData => {
    return {
      time: safeSecondToMillisecond(data.t[0]),
      open: Number(data.o[0]),
      high: Number(data.h[0]),
      low: Number(data.l[0]),
      close: Number(data.c[0]),
      volume: Number(data.v[0]),
    }
  }

  const listenKline = () => {
    doubleMSocketService.onMessage<SocketResponse.KlineResponse>(SocketEventName.Kline, (response) => {
      if (Number(response.params.p) !== currentSymbolId.value) return
      currentKline.value = formatSocketKline(response.data)
      loading.value = false
      loadingHistoryKline.value = false
      if (!previousSpotKline.value) {
        klineDirection.value = 'equal'
      } else if (currentKline.value.close > previousSpotKline.value.close) {
        klineDirection.value = 'up'
      } else if (currentKline.value.close < previousSpotKline.value.close) {
        klineDirection.value = 'down'
      }
      previousSpotKline.value = currentKline.value
    })
  }

  /** 订阅盘口 */
  const subscribeHandicap = () => {
    if (!currentSymbolId.value) return
    doubleMSocketService.emit('Topic.sub', {
      type: 'depth',
      symbol: currentSymbolId.value,
      app_id: envStore.appId,
    })
  }

  /** 取消订阅盘口 */
  const unsubscribeHandicap = (symbolId: SymbolId) => {
    doubleMSocketService.emit('Topic.unsub', {
      type: 'depth',
      symbol: symbolId,
    })
  }

  /** 订阅实时成交 */
  const subscribeRealTimeTransaction = () => {
    if (!currentSymbolId.value) return
    doubleMSocketService.emit('Topic.sub', {
      type: 'order',
      symbol: currentSymbolId.value,
      app_id: envStore.appId,
    })
  }

  /** 取消订阅实时成交 */
  const unsubscribeRealTimeTransaction = (symbolId: SymbolId) => {
    doubleMSocketService.emit('Topic.unsub', {
      type: 'order',
      symbol: symbolId,
    })
  }

  /** 定于K线行情？好奇怪，和App沟通说是遗留问题，如果不订阅，深度图和实时成交不会触发返回 */
  const subscribeMarket = () => {
    doubleMSocketService.emit('Topic.sub', {
      type: 'market',
      symbol: currentSymbolId.value!.toString(),
    })
  }

  /** 取消订阅K线行情 */
  const unsubscribeMarket = (symbolId: SymbolId) => {
    doubleMSocketService.emit('Topic.unsub', {
      type: 'market',
      symbol: symbolId,
    })
  }

  /** 订阅K线 */
  const subscribeKline = () => {
    doubleMSocketService.emit('Topic.sub', {
      type: 'kline',
      symbol: currentSymbolId.value!.toString(),
      resolution: currentResolution.value,
      app_id: envStore.appId,
    })
  }

  /** 设置订阅K线参数 */
  const setSubscribeKlineParams = (symbolId: SymbolId, resolution: ResolutionString) => {
    currentSymbolId.value = symbolId
    currentResolution.value = resolution
  }

  const setResolution = (resolution: ResolutionString) => {
    resetHistoryKlineLoading()
    currentResolution.value = resolution
  }

  onBeforeUnmount(() => {})

  const getSymbolName = () => {
    if (!currentSymbolId.value) return ''
    const name = baseInfoStore.getContractSymbolName(currentSymbolId.value)
    return name
  }

  const getFromSymbolName = () => {
    if (!currentSymbolId.value) return ''
    const symbol = exchangeInfoStore.getContractSymbolInfoBySymbolId(currentSymbolId.value)
    if (!symbol) return ''
    const itemId = symbol.contractItemId
    const baseInfo = baseInfoStore.getCoinInfoByItemId(itemId)
    if (!baseInfo) return ''
    return baseInfo.itemName.toUpperCase()
  }

  const getToSymbolName = () => {
    if (!currentSymbolId.value) return ''
    const symbol = exchangeInfoStore.getContractSymbolInfoBySymbolId(currentSymbolId.value)
    if (!symbol) return ''
    const itemId = symbol.marginItemId
    const baseInfo = baseInfoStore.getCoinInfoByItemId(itemId)
    if (!baseInfo) return ''
    return baseInfo.itemName.toUpperCase()
  }

  /** 价格精度 */
  const coinToPrecision = computed(() => {
    if (!currentSymbolId.value) return 2
    const data = exchangeInfoStore.getContractSymbolInfoBySymbolId(currentSymbolId.value)
    if (!data) return 2
    return data.pricePrecision
  })

  const coinFromPrecision = computed(() => {
    if (!currentSymbolId.value) return 2
    const data = exchangeInfoStore.getContractSymbolInfoBySymbolId(currentSymbolId.value)
    if (!data) return 2
    return data.numberPrecision
  })

  const setHandicapType = (type: HandicapType) => {
    handicapType.value = type
  }

  const reset = () => {
    resetHistoryKlineLoading()
    loading.value = true
    previousSpotKline.value = undefined
    currentKline.value = undefined
    kline.value = []
    toTime.value = 0
  }

  const unsubscribeKline = (oldSymbolId?: SymbolId, resolution?: ResolutionString) => {
    if (!oldSymbolId) return
    doubleMSocketService.emit('Topic.unsub', {
      type: 'kline',
      symbol: oldSymbolId.toString(),
      resolution: resolution || currentResolution.value,
      app_id: envStore.appId,
    })
  }

  const unsubscribeKlineWithResolution = (resolution: ResolutionString) => {
    doubleMSocketService.emit('Topic.unsub', {
      type: 'kline',
      symbol: currentSymbolId.value!.toString(),
      resolution: resolution,
      app_id: envStore.appId,
    })
  }

  const unsubscribeAll = (symbolId?: SymbolId) => {
    if (!symbolId) return
    unsubscribeHandicap(symbolId)
    unsubscribeRealTimeTransaction(symbolId)
    unsubscribeMarket(symbolId)
    unsubscribeKline(symbolId)
    reset()
  }

  const updateSymbolId = (symbolId: SymbolId) => {
    currentSymbolId.value = symbolId
  }

  const setToTime = (time: number) => {
    toTime.value = time
  }

  useContractChangeSymbol((_, oldSymbolId) => {
    unsubscribeAll(oldSymbolId)
  })

  return {
    kline,
    getHistoryKline,
    formatSocketKline,
    listenKline,
    klineDirection,
    currentKline,
    loading,
    loadingHistoryKline,
    setSubscribeKlineParams,
    currentSymbolId,
    currentResolution,
    subscribeHandicap,
    subscribeKline,
    getSymbolName,
    coinToPrecision,
    coinFromPrecision,
    handicapType,
    setHandicapType,
    setResolution,
    resetHistoryKlineLoading,
    toTime,
    setToTime,
    getFromSymbolName,
    getToSymbolName,
    unsubscribeAll,
    updateSymbolId,
    unsubscribeKline,
    subscribeMarket,
    subscribeRealTimeTransaction,
    unsubscribeKlineWithResolution,
  }
})
